import React, { useEffect, useState } from 'react';
import { Calculator, Loader2 } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const formatCurrency = (value) => `€${Number(value || 0).toFixed(2)}`;
const formatPct = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-PT');
  } catch {
    return value;
  }
};

const REGIME_LABELS = {
  art_53: 'Art. 53',
  normal: 'Regime Normal',
  unknown: '—',
};

export default function FiscalView() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/fiscal/dashboard');
      setDashboard(res.data?.data || null);
    } catch {
      toast.error('Erro ao carregar dashboard fiscal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const close = async () => {
    setBusy(true);
    try {
      await api.post('/fiscal/close');
      toast.success('Fechamento fiscal executado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao fechar período fiscal');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Motor Fiscal...</p>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const drivers = dashboard?.drivers || [];
  const alerts = dashboard?.threshold_alerts || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Motor Fiscal</h1>
        <Button onClick={close} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator size={16} className="mr-2" />}
          Fechar Período
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard title="Motoristas com Perfil" value={summary.total_with_profile ?? 0} />
        <MetricCard title="Art. 53" value={summary.total_art53 ?? 0} />
        <MetricCard title="Regime Normal" value={summary.total_normal ?? 0} />
        <MetricCard title="Obrigação SS" value={summary.total_ss_obligation ?? 0} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alertas de Limiar Art. 53</CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum motorista próximo do limiar.</p>
          ) : (
            <div className="space-y-3">
              {alerts.map((a) => (
                <div key={a.driver_id} className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">{a.driver_name}</p>
                    <p className="text-xs text-muted-foreground">
                      YTD: {formatCurrency(a.art_53_revenue_ytd)} | Restante: {formatCurrency(a.art_53_threshold_remaining)}
                    </p>
                  </div>
                  <div className="text-sm font-bold">{formatPct(a.threshold_pct)}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Motoristas — Resumo Fiscal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Motorista</th>
                  <th>NIF</th>
                  <th>Regime</th>
                  <th className="text-right">Receita YTD</th>
                  <th className="text-right">IVA</th>
                  <th className="text-right">SS</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr key={d.id} className="border-b">
                    <td className="py-2 font-medium">
                      {d.first_name} {d.last_name}
                    </td>
                    <td>{d.nif || '—'}</td>
                    <td>{REGIME_LABELS[d.tax_regime] || d.tax_regime || '—'}</td>
                    <td className="text-right">{formatCurrency(d.art_53_revenue_ytd)}</td>
                    <td className="text-right">{formatCurrency(d.iva_collected)}</td>
                    <td className="text-right">{formatCurrency(d.ss_contribution)}</td>
                  </tr>
                ))}
                {drivers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-4 text-center text-sm text-muted-foreground">
                      Nenhum motorista com perfil fiscal.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ title, value }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
